import React, { useRef, useState } from "react";
import axios from "axios";
import { ArrowRightLeft, Copy, Loader2, Sparkles, Volume2, Pause } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Translator() {
  const { t } = useLang();
  const [source, setSource] = useState("it");
  const [target, setTarget] = useState("en");
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const swap = () => {
    setSource(target); setTarget(source);
    setText(result); setResult(text);
  };

  const translate = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setResult("");
    try {
      const { data } = await axios.post(`${API}/ai/translate`, { text, source_lang: source, target_lang: target });
      setResult(data.translation);
    } catch (e) {
      toast.error("Errore di traduzione");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => { navigator.clipboard.writeText(result); toast.success(t.translator.copying); };

  const speak = async () => {
    if (!result || ttsLoading) return;
    if (audioRef.current && playing) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    setTtsLoading(true);
    try {
      const res = await axios.post(`${API}/tts`, { text: result, voice: "shimmer" }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setPlaying(true);
      audio.onended = () => setPlaying(false);
      audio.onpause = () => setPlaying(false);
      await audio.play();
    } catch {
      toast.error("Errore audio");
    } finally {
      setTtsLoading(false);
    }
  };

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E4EAF7] text-xs font-bold text-[#0B3D91] mb-3">
            <Sparkles size={12} className="text-[#F97316]" /> AI POWERED
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0B3D91]" data-testid="translator-title">{t.translator.title}</h1>
          <p className="mt-2 text-[#5A6A94]">{t.translator.subtitle}</p>
        </div>

        <div className="card-elevated p-6 sm:p-8 fade-up">
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#8494B8] uppercase tracking-wider font-bold">{t.translator.from}</span>
              <select value={source} onChange={(e) => setSource(e.target.value)} className="warm-input !py-2 !px-3 text-sm w-36" data-testid="translator-source-lang">
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <button onClick={swap} className="btn-ghost !p-2" data-testid="translator-swap-btn" aria-label="Swap"><ArrowRightLeft size={18} /></button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#8494B8] uppercase tracking-wider font-bold">{t.translator.to}</span>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="warm-input !py-2 !px-3 text-sm w-36" data-testid="translator-target-lang">
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
          </div>

          <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t.translator.inputPlaceholder}
            className="warm-input min-h-[140px] resize-none" data-testid="translator-input" />

          <button onClick={translate} disabled={loading || !text.trim()} className="btn-primary w-full mt-4 !py-3 disabled:opacity-50" data-testid="translator-submit-btn">
            {loading ? <><Loader2 className="animate-spin" size={18} /> {t.translator.loading}</> : t.translator.translate}
          </button>

          {(result || loading) && (
            <div className="mt-6 p-5 rounded-xl brand-sky-bg border border-[#DDE4F5] relative" data-testid="translator-result">
              {loading ? (
                <div className="flex items-center gap-2 text-[#0B3D91]"><Loader2 className="animate-spin" size={16} /> {t.translator.loading}</div>
              ) : (
                <>
                  <p className="text-[#0F1E3D] whitespace-pre-wrap pr-24">{result}</p>
                  <div className="absolute top-3 right-3 flex gap-1">
                    <button onClick={speak} disabled={ttsLoading} className="btn-ghost !p-2" data-testid="translator-tts-btn" title={t.translator.listen}>
                      {ttsLoading ? <Loader2 className="animate-spin" size={16} /> : playing ? <Pause size={16} /> : <Volume2 size={16} />}
                    </button>
                    <button onClick={copy} className="btn-ghost !p-2" data-testid="translator-copy-btn" aria-label="Copy"><Copy size={16} /></button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
